import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Form, Row, Col, Badge, Modal, ProgressBar } from 'react-bootstrap';
import { PlusIcon, EditIcon, TrashIcon, EyeIcon } from '../common/Icons';
import { checkbookService } from '../../services/api';
import { toast } from 'react-toastify';
import CheckbookForm from './CheckbookForm';
import CheckList from './CheckList';

interface Checkbook {
  id: number;
  name: string;
  bank_name: string;
  account_number: string;
  start_number: string;
  end_number: string;
  total_checks: number;
  status: string;
  used_checks_count: number;
  unused_checks_count: number;
  created_at: string;
}

const CheckbookList: React.FC = () => {
  const [checkbooks, setCheckbooks] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [checkbookToDelete, setCheckbookToDelete] = useState<Checkbook | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showChecksModal, setShowChecksModal] = useState(false);
  const [selectedCheckbook, setSelectedCheckbook] = useState<Checkbook | null>(null);

  const fetchCheckbooks = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (statusFilter) params.status = statusFilter;

      const response = await checkbookService.getAll(params);
      setCheckbooks(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des carnets de chèques');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCheckbooks();
  }, [statusFilter]);

  const handleDelete = async () => {
    if (!checkbookToDelete) return;

    try {
      await checkbookService.delete(checkbookToDelete.id);
      toast.success('Carnet de chèques supprimé avec succès');
      fetchCheckbooks();
      setShowDeleteModal(false);
      setCheckbookToDelete(null);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      active: { bg: 'success', text: 'Actif' },
      completed: { bg: 'info', text: 'Terminé' },
      cancelled: { bg: 'danger', text: 'Annulé' },
    };
    const config = statusMap[status as keyof typeof statusMap] || { bg: 'secondary', text: status };
    return <Badge bg={config.bg}>{config.text}</Badge>;
  };

  const getProgressPercentage = (used: number, total: number) => {
    return total > 0 ? Math.round((used / total) * 100) : 0;
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Gestion des carnets de chèques</h1>
        <Button variant="primary" onClick={() => setShowAddModal(true)}>
          <PlusIcon className="me-2" />
          Nouveau carnet
        </Button>
      </div>

      <Card>
        <Card.Header>
          <Row>
            <Col md={4}>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">Tous les statuts</option>
                <option value="active">Actif</option>
                <option value="completed">Terminé</option>
                <option value="cancelled">Annulé</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center">Chargement...</div>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Banque</th>
                  <th>Compte</th>
                  <th>Numéros</th>
                  <th>Total</th>
                  <th>Utilisés</th>
                  <th>Progression</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {checkbooks?.data.map((checkbook: Checkbook) => (
                  <tr key={checkbook.id}>
                    <td>{checkbook.name}</td>
                    <td>{checkbook.bank_name}</td>
                    <td>{checkbook.account_number}</td>
                    <td>{checkbook.start_number} - {checkbook.end_number}</td>
                    <td>{checkbook.total_checks}</td>
                    <td>{checkbook.used_checks_count}</td>
                    <td>
                      <ProgressBar 
                        now={getProgressPercentage(checkbook.used_checks_count, checkbook.total_checks)}
                        label={`${getProgressPercentage(checkbook.used_checks_count, checkbook.total_checks)}%`}
                        style={{ minWidth: '100px' }}
                      />
                    </td>
                    <td>{getStatusBadge(checkbook.status)}</td>
                    <td>
                      <Button 
                        variant="outline-info" 
                        size="sm" 
                        className="me-1"
                        onClick={() => {
                          setSelectedCheckbook(checkbook);
                          setShowChecksModal(true);
                        }}
                      >
                        <EyeIcon />
                      </Button>
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="me-1"
                        onClick={() => {
                          setSelectedCheckbook(checkbook);
                          setShowEditModal(true);
                        }}
                      >
                        <EditIcon />
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => {
                          setCheckbookToDelete(checkbook);
                          setShowDeleteModal(true);
                        }}
                      >
                        <TrashIcon />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmer la suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Êtes-vous sûr de vouloir supprimer le carnet "{checkbookToDelete?.name}" ?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Annuler
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Supprimer
          </Button>
        </Modal.Footer>
      </Modal>

      <CheckbookForm
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSuccess={fetchCheckbooks}
      />

      <CheckbookForm
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedCheckbook(null);
        }}
        onSuccess={() => {
          fetchCheckbooks();
          setShowEditModal(false);
          setSelectedCheckbook(null);
        }}
        checkbook={selectedCheckbook}
      />

      <CheckList
        show={showChecksModal}
        onHide={() => {
          setShowChecksModal(false);
          setSelectedCheckbook(null);
        }}
        checkbook={selectedCheckbook}
      />
    </div>
  );
};

export default CheckbookList;